import { isWeb } from "@memorang/helpers/platform";
import { useEffect, useRef } from "react";
import { Text } from "react-native-paper";
import type { FontSchemaType } from "../../font";
import { useAppTheme } from "../../useAppTheme";
import { useDevice } from "../../useDevice";
import Box from "../Box";
import { TextSkeleton } from "../ChatUI/TextSkeleton";
import { CustomListSubheader } from "../CustomListSubheader";
import RenderMarkdown from "../MarkdownRenderer";

type Props = {
	aiResponse?: string;
	loading?: boolean;
	renderTtsContainer?: (explanation?: string) => React.ReactNode;
	renderFeedbackMenu?: () => React.ReactNode;
	explanationTitle: string;
	appFont: FontSchemaType;
	fontSize: number;
	persistedAiFeedback?: string;
	currentAiFeedback?: string;
	emptyFeedbackText?: string;
};
const Wrapper = isWeb ? "div" : Box;

const DynamicExplanation = ({
	aiResponse,
	loading,
	renderTtsContainer,
	renderFeedbackMenu,
	explanationTitle,
	appFont,
	fontSize,
	persistedAiFeedback,
	currentAiFeedback,
	emptyFeedbackText,
}: Props) => {
	const { isDesktopWeb } = useDevice();
	const theme = useAppTheme();
	const explanationContainerRef = useRef<HTMLDivElement | null>(null);
	const explanation = currentAiFeedback || persistedAiFeedback;
	const prevAiResponseRef = useRef(aiResponse);

	useEffect(() => {
		const hadResponse = Boolean(prevAiResponseRef.current);
		prevAiResponseRef.current = aiResponse;
		if (aiResponse && !hadResponse && loading && isDesktopWeb) {
			explanationContainerRef.current?.scrollIntoView({
				behavior: "smooth",
				block: "center",
			});
		}
	}, [aiResponse, loading, isDesktopWeb]);

	if (loading && !aiResponse) {
		return (
			<Wrapper>
				<Box
					flexDirection="row"
					alignItems="center"
					justifyContent="space-between"
					height={34}
				>
					<CustomListSubheader enableGutters={false} font={appFont} dense>
						{explanationTitle}
					</CustomListSubheader>
				</Box>
				<TextSkeleton numLines={4} height={20} />
			</Wrapper>
		);
	}
	return (
		<Wrapper ref={explanationContainerRef}>
			<Box
				flexDirection="row"
				alignItems="center"
				justifyContent="space-between"
			>
				<CustomListSubheader enableGutters={false} font={appFont} dense>
					{explanationTitle}
				</CustomListSubheader>
				<Box flexDirection="row" alignItems="center" gap={6}>
					{renderTtsContainer?.(explanation)}
					{renderFeedbackMenu?.()}
				</Box>
			</Box>
			{!explanation && !loading && emptyFeedbackText ? (
				<Text
					variant="bodyMedium"
					style={{
						fontStyle: "italic",
						color: theme.colors.onSurfaceVariant,
					}}
				>
					{emptyFeedbackText}
				</Text>
			) : (
				<RenderMarkdown
					text={explanation}
					key={explanation}
					appFont={appFont}
					variant="explanation"
					fontSize={fontSize}
					customStyles={{
						paragraph: {
							lineHeight: 22,
							marginBottom: 8,
						},
					}}
					paragraphMarginVertical={6}
				/>
			)}
			{loading ? <TextSkeleton numLines={2} height={20} /> : null}
		</Wrapper>
	);
};

export default DynamicExplanation;
